import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import { compose } from 'redux';
import { useTranslation } from 'react-i18next';
import { Grid, Field } from 'nallian-shared-ui/lib/components';
import { SelectFieldValueComponent } from 'nallian-shared-ui/lib/components/redux-form-semantic-ui';
import { FormModal } from 'nallian-shared-ui/lib/modules';
import relationTypeService from '../../../relationTypes/services/relationTypeService';
import relationTypesService from '../../../relationTypes/services/relationTypesService';
import sourcesService from '../../services/relations/sourcesService';
import targetsService from '../../services/relations/targetsService';
import { generateErrorsForRequiredFields } from '../../../app/lib/validationHelper';
import { communityMemberOptionMap } from '../../helpers/communityMemberOptionMap';

const FORM_NAME = 'relation';
const REQUIRED_FIELDS = ['relationTypeId', 'sourceId', 'targetId'];

const validate = values => {
  let errors = {};
  errors = generateErrorsForRequiredFields(REQUIRED_FIELDS, values, errors);

  return errors;
};

const RelationForm = ({
  submitProcessing,
  dataLoading,
  data,
  returnRoute,
  onSubmit,
  header,
  dispatchRelationTypeGet,
  relationTypeFetch,
  communityId,
  dispatchRelationTypesGet,
  relationTypesFetch,
  dispatchSourcesGet,
  sourcesFetch,
  dispatchTargetsGet,
  targetsFetch,
}) => {
  const { t } = useTranslation();

  const relationTypeId = useSelector(state => get(state, 'form.relation.values.relationTypeId'));

  useEffect(() => dispatchRelationTypesGet(t), [dispatchRelationTypesGet, t]);

  useEffect(() => {
    if (relationTypeId) dispatchRelationTypeGet(relationTypeId, undefined, t);
  }, [relationTypeId, dispatchRelationTypeGet, t]);

  const relationTypeOptions = useMemo(
    () =>
      (relationTypesFetch.value || []).map(rt => ({
        key: rt.id,
        value: rt.id,
        text: rt.name,
      })),
    [relationTypesFetch.value]
  );

  const sourceOrganisationRoleIds = useMemo(
    () => (relationTypeFetch.value?.sourceRestrictions || []).map(sr => sr.organisationRole?.id),
    [relationTypeFetch.value]
  );

  const targetOrganisationRoleIds = useMemo(
    () => (relationTypeFetch.value?.targetRestrictions || []).map(tr => tr.organisationRole?.id),
    [relationTypeFetch.value]
  );

  useEffect(() => dispatchSourcesGet(communityId, sourceOrganisationRoleIds), [
    dispatchSourcesGet,
    communityId,
    sourceOrganisationRoleIds,
  ]);

  useEffect(() => dispatchTargetsGet(communityId, targetOrganisationRoleIds), [
    dispatchTargetsGet,
    communityId,
    targetOrganisationRoleIds,
  ]);

  const sourceOptions = useMemo(() => (sourcesFetch.value || []).map(communityMemberOptionMap), [
    sourcesFetch.value,
  ]);

  const targetOptions = useMemo(() => (targetsFetch.value || []).map(communityMemberOptionMap), [
    targetsFetch.value,
  ]);

  return (
    <FormModal
      closeIcon
      header={header}
      dataLoading={dataLoading}
      submitProcessing={submitProcessing}
      onSubmit={onSubmit}
      data={data}
      t={t}
      validate={validate}
      form={FORM_NAME}
      cancelRoute={returnRoute}
      cancelButtonContent={t('cancel')}
      saveButtonIcon="save"
      saveButtonContent={t('save')}
    >
      <Grid>
        <Grid.Column mobile={16} tablet={16} computer={16}>
          <Field
            fluid
            label={t('relationType')}
            name="relationTypeId"
            options={relationTypeOptions}
            valueComponent={SelectFieldValueComponent}
            disabled={submitProcessing || dataLoading}
          />
        </Grid.Column>
        {relationTypeFetch.value && (
          <Grid.Column mobile={8} tablet={8} computer={8}>
            <Field
              fluid
              clearable
              search
              label={t('source')}
              name="sourceId"
              options={sourceOptions}
              valueComponent={SelectFieldValueComponent}
              disabled={submitProcessing || dataLoading}
            />
          </Grid.Column>
        )}
        {relationTypeFetch.value && (
          <Grid.Column mobile={8} tablet={8} computer={8}>
            <Field
              fluid
              clearable
              search
              label={t('target')}
              name="targetId"
              options={targetOptions}
              valueComponent={SelectFieldValueComponent}
              disabled={submitProcessing || dataLoading}
            />
          </Grid.Column>
        )}
      </Grid>
    </FormModal>
  );
};

RelationForm.propTypes = {
  dispatchSourcesGet: PropTypes.func.isRequired,
  sourcesFetch: PropTypes.object.isRequired,
  dispatchTargetsGet: PropTypes.func.isRequired,
  targetsFetch: PropTypes.object.isRequired,
  dispatchRelationTypesGet: PropTypes.func.isRequired,
  relationTypesFetch: PropTypes.object.isRequired,
  communityId: PropTypes.string.isRequired,
  dispatchRelationTypeGet: PropTypes.func.isRequired,
  relationTypeFetch: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  dataLoading: PropTypes.bool,
  submitProcessing: PropTypes.bool,
  header: PropTypes.string.isRequired,
  returnRoute: PropTypes.string.isRequired,
  data: PropTypes.object,
  editMode: PropTypes.bool,
};

RelationForm.defaultProps = {
  dataLoading: false,
  editMode: false,
  submitProcessing: false,
  data: {},
};

export default compose(
  relationTypesService,
  relationTypeService,
  sourcesService,
  targetsService
)(RelationForm);
